/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { Suspense, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useLocation,
} from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { GlobalStyles } from 'src/GlobalStyles';
import ErrorBoundary from 'src/components/ErrorBoundary';
import Loading from 'src/components/Loading';
import Menu from 'src/features/home/Menu';
import getBootstrapData from 'src/utils/getBootstrapData';
import ToastContainer from 'src/components/MessageToasts/ToastContainer';
import setupApp from 'src/setup/setupApp';
import setupPlugins from 'src/setup/setupPlugins';
import { routes, isFrontendRoute } from 'src/views/routes';
import { Logger, LOG_ACTIONS_SPA_NAVIGATION } from 'src/logger/LogUtils';
import setupExtensions from 'src/setup/setupExtensions';
import { logEvent } from 'src/logger/actions';
import { store } from 'src/views/store';
import SearchPagePanel from 'src/backstage/components/SeachPagePanel/SearchPagePanel';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';
import { RootContextProviders } from './RootContextProviders';
import { ScrollToTop } from './ScrollToTop';
import 'src/assets/stylesheets/tailwind.css';
import isMobile from '../utils/isMobile';
import { getBehaviors } from 'src/views/behaviorSlice';
import { showRoute } from 'src/utils/route';
import { useAppSelector } from 'src/hooks/hooks';

setupApp();
setupPlugins();
setupExtensions();

const bootstrapData = getBootstrapData();

let lastLocationPathname: string;

const boundActions = bindActionCreators({ logEvent }, store.dispatch);

const LocationPathnameLogger = () => {
  const location = useLocation();
  useEffect(() => {
    // This will log client side route changes for single page app user navigation
    boundActions.logEvent(LOG_ACTIONS_SPA_NAVIGATION, {
      path: location.pathname,
    });
    // reset performance logger timer start point to avoid soft navigation
    // cause dashboard perf measurement problem
    if (lastLocationPathname && lastLocationPathname !== location.pathname) {
      Logger.markTimeOrigin();
    }
    lastLocationPathname = location.pathname;
  }, [location.pathname]);
  return <></>;
};

const RoutesContainer = () => {
  const { behaviorKeys } = useAppSelector(state => state.behaviorReducer);
  return (
    <Switch>
      {routes
        .filter(route => showRoute(route, behaviorKeys))
        .map(({ path, Component, props = {}, Fallback = Loading }) => (
          <Route path={path} key={path}>
            <Suspense fallback={<Fallback />}>
              <ErrorBoundary>
                <Component user={bootstrapData.user} {...props} />
              </ErrorBoundary>
            </Suspense>
          </Route>
        ))}
    </Switch>
  );
};

const App = () => {
  function hijackHistoryMethod(type: 'pushState' | 'replaceState') {
    const original = history[type];
    history[type] = function (...args: any[]) {
      const result = original.apply(this, args);
      const event = new Event(type.toLowerCase());
      window.dispatchEvent(event);
      return result;
    };
  }
  hijackHistoryMethod('pushState');
  hijackHistoryMethod('replaceState');

  useEffect(() => {
    document.title = 'ai-bi-helper';
  }, []);
  useEffect(() => {
    // @ts-ignore
    store.dispatch(getBehaviors());
  }, [store.dispatch]);

  const boolean = () => {
    const pathList = ['/conversation-share', '/aibi/login', '/mobile', '/topic-share/public'];
    const { pathname, search } = window.location;
    if (pathList.find(path => pathname.includes(path))) {
      return false;
    } else if (
      (!bootstrapData.user || !bootstrapData.user.userId) &&
      search.includes('share_type=3')
    ) {
      // 图表、看板或分析报告校外公开，不显示顶部菜单栏
      return false;
    } else {
      return true;
    }
  };
  const [showMenu, setShowMenu] = useState(boolean());
  useEffect(() => {
    const handlePathChange = () => {
      setShowMenu(boolean());
    };

    window.addEventListener('popstate', handlePathChange);
    window.addEventListener('pushstate', handlePathChange);
    window.addEventListener('replacestate', handlePathChange);

    return () => {
      window.removeEventListener('popstate', handlePathChange);
      window.removeEventListener('pushstate', handlePathChange);
      window.removeEventListener('replacestate', handlePathChange);
    };
  }, []);

  const mobile = isMobile();

  return (
    <div
      className={`basis-full h-full flex-1 flex flex-col ${
        mobile ? '' : 'web'
      }`}
    >
      <Router>
        <ScrollToTop />
        <LocationPathnameLogger />
        <ConfigProvider locale={zhCN}>
          <RootContextProviders>
            <GlobalStyles />
            {showMenu && <SearchPagePanel />}
            {showMenu && (
              <Menu
                user={bootstrapData.user}
                data={bootstrapData.common.menu_data}
                isFrontendRoute={isFrontendRoute}
              />
            )}
            <RoutesContainer />
            <ToastContainer />
          </RootContextProviders>
        </ConfigProvider>
      </Router>
    </div>
  );
};

export default hot(App);
